import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authApi, ApiError } from '../api';
import { useTheme, type ClientTheme } from '../themes/ThemeContext';

export function ResetPasswordPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (token) {
    return <ConfirmResetForm token={token} theme={theme} />;
  }

  return <RequestResetForm theme={theme} />;
}

function RequestResetForm({ theme }: { theme: ClientTheme }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.requestPasswordReset(email);
    } catch {
      // Always show success to prevent email enumeration
    } finally {
      setSent(true);
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Check Your Email</h1>
          <p>If an account with that email exists, we sent a password reset link.</p>
          <a href="/login" style={{ color: theme.colors.primary, marginTop: 16, display: 'inline-block' }}>Back to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={theme.logoUrl} alt={theme.displayName} style={{ height: 48, marginBottom: 16 }} />
          <h1 style={{ fontSize: 24, fontWeight: 600 }}>Reset Password</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ width: '100%', padding: 12, borderRadius: 6, border: 'none', background: theme.colors.primary, color: '#fff', fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 14 }}>
          <a href="/login" style={{ color: theme.colors.primary }}>Back to Login</a>
        </div>
      </div>
    </div>
  );
}

function ConfirmResetForm({ token, theme }: { token: string; theme: ClientTheme }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 10) {
      setError('Password must be at least 10 characters');
      return;
    }

    setLoading(true);

    try {
      await authApi.confirmPasswordReset(token, newPassword);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_token') {
          setError('This reset link has expired or is invalid. Please request a new one.');
        } else if (err.code === 'password_breached') {
          setError('This password has been found in a data breach. Please choose a different one.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Password Reset</h1>
          <p>Your password has been reset successfully.</p>
          <a href="/login" style={{ color: theme.colors.primary, marginTop: 16, display: 'inline-block' }}>Log In</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={theme.logoUrl} alt={theme.displayName} style={{ height: 48, marginBottom: 16 }} />
          <h1 style={{ fontSize: 24, fontWeight: 600 }}>Set New Password</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="newPassword" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>New Password</label>
            <input
              id="newPassword"
              type="password"
              required
              minLength={10}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>At least 10 characters</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>

          {error && (
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ width: '100%', padding: 12, borderRadius: 6, border: 'none', background: theme.colors.primary, color: '#fff', fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
